import { Injectable, OnDestroy, Inject } from '@angular/core';

import { Observable, Subject, timer, Observer, BehaviorSubject } from 'rxjs';
import { tap, catchError, filter, switchMap } from 'rxjs/operators';
import { webSocket, WebSocketSubject, WebSocketSubjectConfig } from 'rxjs/webSocket';

import { WebsocketsOptions } from './config';

export enum ConnectionStatus {
  NotInitialized = 'NotInitialized',
  Opened = 'Opened',
  OpenedAfterRetry = 'OpenedAfterRetry',
  ConnectionRetry = 'ConnectionRetry',
  Closed = 'Closed',
}

@Injectable()
export class CoreWebsocketService<T = unknown> implements OnDestroy {
  connectionStatus = new BehaviorSubject<ConnectionStatus>(ConnectionStatus.NotInitialized);

  private socket!: WebSocketSubject<T>;

  private messages = new Subject<T>();

  constructor(@Inject(WebsocketsOptions) private options: WebsocketsOptions) {}

  ngOnDestroy(): void {
    this.messages.unsubscribe();
  }

  hasSocket(): boolean {
    return !!this.socket;
  }

  getWebSocketSubject() {
    return this.socket;
  }

  create(urlOrConfig: string | WebSocketSubjectConfig<T>): Observable<T> {
    this.close();

    if (typeof urlOrConfig === 'string') {
      this.socket = webSocket({
        url: urlOrConfig,
        openObserver: this._getOpenObserver<T>(urlOrConfig),
        closeObserver: this._getCloseObserver<T>(urlOrConfig),
        closingObserver: this._getClosingObserver<T>(urlOrConfig),
      });
    } else {
      this.socket = webSocket({
        ...urlOrConfig,
        openObserver: this._getOpenObserver<T>(urlOrConfig),
        closeObserver: this._getCloseObserver<T>(urlOrConfig),
        closingObserver: this._getClosingObserver<T>(urlOrConfig),
      });
    }

    return this.socket.asObservable().pipe(
      tap((msg) => {
        this.messages.next(msg);
      }),
      catchError((error, caught) =>
        timer(5000).pipe(
          tap(() => {
            this.connectionStatus.next(ConnectionStatus.ConnectionRetry);

            this._logStatus();
          }),
          switchMap(() => caught),
        ),
      ),
    );
  }

  request<R>(
    msg: T,
    filterFn: (msg: T, value: R) => boolean = () => true,
    unsubMsg: (() => void) | false,
  ): Observable<R> {
    return this.multiplex(
      () => msg,
      unsubMsg,
      (value) => filterFn(msg, <any>value),
    );
  }

  // returns stream of all answers
  stream(): Observable<T> {
    return this.messages.asObservable();
  }

  close(): void {
    if (this.hasSocket()) {
      return this.socket.unsubscribe();
    }
  }

  multiplex(subMsg: () => any, unsubMsg: (() => any) | false, messageFilter: (value: T) => boolean) {
    return new Observable((observer: Observer<any>) => {
      const subscription = this.stream().pipe(filter(messageFilter)).subscribe(observer);

      try {
        this.socket.next(subMsg());
      } catch (err) {
        observer.error(err);
      }

      return () => {
        subscription.unsubscribe();

        if (typeof unsubMsg === 'function') {
          try {
            this.socket.next(unsubMsg());
          } catch (err) {
            observer.error(err);
          }
        }
      };
    });
  }

  private _getOpenObserver<R>(urlOrConfig: string | WebSocketSubjectConfig<R>) {
    return {
      next: () => {
        if (this.connectionStatus.getValue() === ConnectionStatus.ConnectionRetry) {
          this.connectionStatus.next(ConnectionStatus.OpenedAfterRetry);
        } else {
          this.connectionStatus.next(ConnectionStatus.Opened);
        }

        this._logStatus();

        if (urlOrConfig.hasOwnProperty('openObserver')) {
          (<WebSocketSubjectConfig<R>>urlOrConfig).openObserver?.next(undefined as unknown as Event);
        }
      },
    };
  }

  private _getCloseObserver<R>(urlOrConfig: string | WebSocketSubjectConfig<R>) {
    return {
      next: () => {
        this.connectionStatus.next(ConnectionStatus.Closed);

        this._logStatus();

        if (urlOrConfig.hasOwnProperty('closeObserver')) {
          (<WebSocketSubjectConfig<R>>urlOrConfig).closeObserver?.next(undefined as unknown as CloseEvent);
        }
      },
    };
  }

  private _getClosingObserver<R>(urlOrConfig: string | WebSocketSubjectConfig<R>) {
    return {
      next: () => {
        this.connectionStatus.next(ConnectionStatus.Closed);

        this._logStatus();

        if (urlOrConfig.hasOwnProperty('closingObserver')) {
          (<WebSocketSubjectConfig<R>>urlOrConfig).closingObserver?.next(undefined);
        }
      },
    };
  }

  private _logStatus(): void {
    if (this.options.debug) {
      console.log(`Connection status: ${this.connectionStatus.getValue()}`);
    }
  }
}
